"use client";

import { useEffect, useState } from "react";
import "./index.css";
import ApolloLogo from "../img/Apollo_logo.svg";
import Cookies from "js-cookie";
import { useNavigate, useLocation } from "react-router-dom";
import { openDB } from "idb";

export default function Index(props) {
  const { component, appName, doc_name } = props;
  const navigate = useNavigate("/login");
  const [highlight, setHighlight] = useState(0);
  const [showFeedbackPopup, setShowFeedbackPopup] = useState(false);
  const [feedbackText, setFeedbackText] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [feedbackMessage, setFeedbackMessage] = useState("");
  const location = useLocation();

  useEffect(() => {
    if (location.pathname.includes("patient-details")) {
      setHighlight(1);
    } else if (location.pathname.includes("attributes")) {
      setHighlight(2);
    } else if (location.pathname.includes("report")) {
      setHighlight(3);
    } else if (location.pathname.includes("feedback")) {
      setHighlight(4);
    }
  }, [location]);

  const initializeDB = async () => {
    const db = await openDB("ImageStore", 1, {
      upgrade(db) {
        if (!db.objectStoreNames.contains("images")) {
          db.createObjectStore("images", { keyPath: "id" });
        }
      },
    });
    return db;
  };

  const clearImagesOnLogout = async () => {
    try {
      const db = await initializeDB();
      const tx = db.transaction("images", "readwrite");
      const store = tx.objectStore("images");
      await store.clear(); // Clears all records from the 'images' object store
      await tx.done; // Ensure the transaction is completed
    } catch (error) {
      console.error("Error clearing images from IndexedDB:", error);
    }
  };

  const submitFeedback = async () => {
    if (!feedbackText.trim()) {
      setFeedbackMessage("Please enter your feedback before submitting.");
      return;
    }

    setIsSubmitting(true);
    setFeedbackMessage("");

    try {
      const jwt_token = Cookies.get("jwt_token");
      let myHeaders = new Headers();
      myHeaders.append("Content-Type", "application/json");
      myHeaders.append("Authorization", `Bearer ${jwt_token}`);

      const response = await fetch(
        "https://clinical-server-apis.azurewebsites.net/api/feedback",
        {
          method: "POST",
          headers: myHeaders,
          body: JSON.stringify({
            appName: appName,
            feedback: feedbackText,
          }),
        }
      );

      if (response.ok) {
        setFeedbackMessage("Thank you for your feedback!");
        setFeedbackText("");
        setTimeout(() => {
          setShowFeedbackPopup(false);
          setFeedbackMessage("");
        }, 2000);
      } else {
        setFeedbackMessage("Failed to submit feedback. Please try again.");
      }
    } catch (error) {
      console.error("Error submitting feedback:", error);
      setFeedbackMessage("An error occurred. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const openFeedbackPopup = () => {
    setShowFeedbackPopup(true);
    setFeedbackText("");
    setFeedbackMessage("");
  };

  const closeFeedbackPopup = () => {
    setShowFeedbackPopup(false);
    setFeedbackText("");
    setFeedbackMessage("");
  };

  const logOut = async () => {
    await clearImagesOnLogout();
    Cookies.remove("jwt_token");
    Cookies.remove("sas_token");
    localStorage.clear();
    navigate("/login");
  };

  const returnToHome = () => {
    navigate("/");
  };

  return (
    <>
      <nav id="navBar_PDPage" style={{ backgroundColor: props.surface_color }}>
        <div id="ApolloLogoBox_PDPage">
          <img src={ApolloLogo || "/placeholder.svg"} alt="" />
        </div>
        <h1 id="titleNameOfApplication_PDPage" style={{ color: props.color }}>
          {appName}
        </h1>
      </nav>
      <main id="bodyContentContainer">
        <nav
          className="outframe-heading-bar-container"
          style={{ backgroundColor: props.surface_color }}
        >
          <ul className="list-of-redirect-elements">
            <div className="landingpage-action-icons">
              <div
                id="about-icon"
                className="app-landing-page-icon-container"
                style={{
                  backgroundColor:
                    highlight === 1 ? props.surface_highlight_color : "",
                }}
              >
                <img
                  src="/folder.svg"
                  alt="about-icon"
                  className="icon-landing"
                />
              </div>
              <li className="nav-link-buttons">Details</li>
            </div>
            <div className="landingpage-action-icons">
              <div
                id="research-icon"
                className="app-landing-page-icon-container"
                style={{
                  backgroundColor:
                    highlight === 3 ? props.surface_highlight_color : "",
                }}
              >
                <img
                  src="/chart.jpg"
                  alt="research-icon"
                  className="icon-landing"
                />
              </div>
              <li className="nav-link-buttons">Report</li>
            </div>
            {(appName === "AICOPD REPORT" ||
              appName === "AILF REPORT" ||
              appName === "PREDIABETES AI REPORT" ||
              appName === "AICVD REPORT" ||
              appName === "AIHYPERTENTION REPORT" ||
              appName === "AICKD REPORT") && (
                <div className="landingpage-action-icons">
                  <div
                    id="feedback-icon"
                    className="app-landing-page-icon-container"
                    onClick={openFeedbackPopup}
                    style={{
                      backgroundColor:
                        highlight === 4 ? props.surface_highlight_color : "",
                      cursor: "pointer",
                    }}
                  >
                    <img
                      src="/feedback.svg"
                      alt="feedback-icon"
                      className="icon-landing"
                    />
                  </div>
                  <li className="nav-link-buttons">Feedback</li>
                </div>
              )}
          </ul>
          <div className="document-container">
            <a
              className="doc-element"
              target="_blank"
              rel="noreferrer"
              href={
                doc_name === "ailf"
                  ? "/IFU-AILF-2026.pdf"
                  : doc_name === "prediabetes"
                    ? "/IFU-Prediabetes-2026.pdf"
                    : doc_name === "aicopd"
                      ? "/IFU-AICOPD-2026.pdf"
                      : doc_name === "hypertension"
                        ? "/IFU-AI-Hypertension-2026.pdf"
                        : doc_name === "ckd"
                          ? "/IFU-AICKD-2026.pdf"
                          : "/IFU-AICVD-2026.pdf"
              }
            >
              <img
                className="doc-icon"
                src="/icons8-pdf-48.png"
                alt="doc.icon"
              />
             IFU
            </a>
            <a className="doc-element" href="/Incident_Report_Form_2026.docx" download>
              <img
                className="doc-icon"
                src="/icons8-microsoft-word-2019-48.png"
                alt="doc.icon"
              />
              Incident Report Form
            </a>
          </div>
          <div className="logout-button-container-block">
            <div className="landingpage-action-icons">
              <div
                className="app-landing-page-icon-container"
                onClick={returnToHome}
              >
                <img
                  src="/icons8-home.svg"
                  alt="home-icon"
                  className="icon-landing"
                />
              </div>
              <button className="logout-button">Home</button>
            </div>
            <div className="landingpage-action-icons">
              <div className="app-landing-page-icon-container" onClick={logOut}>
                <img
                  src="/logout-svgrepo-com.svg"
                  alt="logout-icon"
                  className="icon-landing"
                />
              </div>
              <button className="logout-button">Log Out</button>
            </div>
          </div>
        </nav>
        <div id="rightContentBlock">{component}</div>
      </main>

      {showFeedbackPopup && (
        <div className="feedback-popup-overlay">
          <div className="feedback-popup">
            <div className="feedback-popup-header">
              <h3>Feedback</h3>
              <button
                className="feedback-close-button"
                onClick={closeFeedbackPopup}
              >
                ×
              </button>
            </div>
            <div className="feedback-popup-content">
              <textarea
                className="feedback-textarea"
                placeholder="Please share your feedback, suggestions, or report any issues..."
                value={feedbackText}
                onChange={(e) => setFeedbackText(e.target.value)}
                rows={6}
                disabled={isSubmitting}
              />
              {feedbackMessage && (
                <div
                  className={`feedback-message ${feedbackMessage.includes("Thank you") ? "success" : "error"
                    }`}
                >
                  {feedbackMessage}
                </div>
              )}
            </div>
            <div className="feedback-popup-actions">
              <button
                className="feedback-submit-button"
                onClick={submitFeedback}
                disabled={isSubmitting || !feedbackText.trim()}
              >
                {isSubmitting ? "Submitting..." : "Submit Feedback"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
